import React, { createContext, useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  // Token validation utilities
  const isTokenValid = (user) => {
    if (!user || !user.access_token) return false;
    
    try {
      // Decode JWT token to check expiration
      const tokenPayload = JSON.parse(atob(user.access_token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Check if token is expired (with 30 second buffer for safety)
      return tokenPayload.exp > (currentTime + 30);
    } catch (error) {
      logger.error('Error validating token:', error);
      return false;
    }
  };

  const getTokenExpirationTime = (user) => {
    if (!user || !user.access_token) return null;
    
    try {
      const tokenPayload = JSON.parse(atob(user.access_token.split('.')[1]));
      return new Date(tokenPayload.exp * 1000);
    } catch (error) {
      logger.error('Error getting token expiration:', error);
      return null;
    }
  };

  // Enhanced logout function with conversation memory control
  const logout = useCallback((reason = null, clearMemory = false) => {
    // Get user email from localStorage directly to avoid dependency on currentUser
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    const userEmail = storedUser?.email;

    logger.debug('Logout initiated', { reason, clearMemory });

    // Always clear auth data
    localStorage.removeItem('user');
    setCurrentUser(null);
    setIsTokenExpired(false);
    setError(null);

    // Clear conversation memory only if explicitly requested
    if (clearMemory && userEmail) {
      logger.debug('Clearing conversation memory for authenticated user');

      const aiCoachKey = `rlc-aicoach-state-${userEmail}`;
      const reportWriterKey = `rlc-reportwriter-state-${userEmail}`;

      logger.debug('Removing conversation state keys');

      localStorage.removeItem(aiCoachKey);
      localStorage.removeItem(reportWriterKey);
      localStorage.removeItem('rlc-aicoach-state-guest');
      localStorage.removeItem('rlc-reportwriter-state-guest');

      logger.debug('Memory cleared successfully');
    } else {
      logger.debug('Preserving conversation memory');
    }

    if (reason === 'token_expired') {
      setIsTokenExpired(true);
      setError('Your session has expired. Please log in again.');
    }

    logger.debug('Logout complete');
  }, []); // No dependencies - reads from localStorage directly

  // Handle authentication errors (401 responses)
  const handleAuthError = useCallback((error, response = null) => {
    if (response?.status === 401 || error?.message?.includes('401') || error?.message?.includes('unauthorized')) {
      logout('token_expired');
      return true; // Indicates this was an auth error
    }
    return false; // Not an auth error
  }, [logout]);

  // Validate current session
  const validateSession = useCallback(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');

    if (user && !isTokenValid(user)) {
      logout('token_expired');
      return false;
    }

    return !!user;
  }, [logout]);

  // Get authenticated headers for API calls
  const getAuthHeaders = useCallback(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');

    if (!user || !isTokenValid(user)) {
      logout('token_expired');
      return null;
    }

    return {
      'Authorization': `Bearer ${user.access_token}`,
      'Content-Type': 'application/json'
    };
  }, [logout]);

  // Make authenticated API call with automatic error handling
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    const headers = getAuthHeaders();

    if (!headers) {
      throw new Error('Session expired. Please log in again.');
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });

      // Handle 401 responses
      if (response.status === 401) {
        handleAuthError(null, response);
        throw new Error('Session expired. Please log in again.');
      }

      return response;
    } catch (error) {
      // Check if it's an auth error
      if (handleAuthError(error)) {
        throw new Error('Session expired. Please log in again.');
      }
      throw error;
    }
  }, [getAuthHeaders, handleAuthError]);

  // Check if user is already logged in on app load
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');

    if (user) {
      if (isTokenValid(user)) {
        setCurrentUser(user);
        setIsTokenExpired(false);
      } else {
        // Token is expired, clean up
        localStorage.removeItem('user');
        setCurrentUser(null);
        setIsTokenExpired(true);
        setError('Your session has expired. Please log in again.');
      }
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Set up token expiration monitoring
  useEffect(() => {
    if (!currentUser) return;

    const checkTokenExpiration = () => {
      if (!isTokenValid(currentUser)) {
        logout('token_expired');
      }
    };

    // Check token every minute
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
  }, [currentUser, logout]);

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      setIsTokenExpired(false);

      const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/v1/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          username: email,
          password: password,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to login');
      }
      
      // Store user data in local storage
      localStorage.setItem('user', JSON.stringify(data));
      setCurrentUser(data);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to register');
      }

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Impersonate tenant function
  const impersonate = async (tenantId) => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      if (!headers) {
        throw new Error('Session expired. Please log in again.');
      }

      const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/v1/auth/impersonate`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ tenant_id: tenantId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to impersonate tenant');
      }

      // Store impersonated user data in local storage
      localStorage.setItem('user', JSON.stringify(data));
      setCurrentUser(data);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Stop impersonation function
  const stopImpersonation = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      if (!headers) {
        throw new Error('Session expired. Please log in again.');
      }

      const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/v1/auth/stop-impersonation`, {
        method: 'POST',
        headers: headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to stop impersonation');
      }

      // Store updated user data in local storage
      localStorage.setItem('user', JSON.stringify(data));
      setCurrentUser(data);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    isTokenExpired,
    login,
    register,
    logout,
    validateSession,
    handleAuthError,
    getAuthHeaders,
    authenticatedFetch,
    impersonate,
    stopImpersonation,
    isTokenValid: () => currentUser ? isTokenValid(currentUser) : false,
    getTokenExpirationTime: () => currentUser ? getTokenExpirationTime(currentUser) : null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};